import { GET_SELLER_UNITS_AND_PROJECTS_MAP_BASED_SEARCH } from "@/gql/map";
import { PROJECT_NEARBY_WITH_SUPS_QUERY } from "@/gql/project";
import { mapDefaults } from "@/info/map-defaults";
import type { Filters } from "@/types/filters";
import type { MapItem, MapItemWithCoodId } from "@/types/search";
import type { ApolloLocation } from "@/utils/location-details-store";
import { logger } from "@/utils/logger";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface Bounds {
	north: number;
	south: number;
	east: number;
	west: number;
}

// Type for raw API response where _id is a string "lng,lat" before parsing
type RawNearbyProject = Omit<MapItemWithCoodId, "_id"> & { _id: string };

interface FetchMapDataParams {
	filters: Partial<Filters>;
	skip?: number;
	limit?: number;
	append?: boolean;
}

export type MapMode = "default" | "project";

export interface MapDataState {
	mapData: MapItemWithCoodId[];
	initialMapData: MapItemWithCoodId[];
	nearByprojects: MapItemWithCoodId[];
	isMapDataFetching: boolean;
	isNearByProjectDataFetching: boolean;
	isTotalCountFetching: boolean;
	isInitialBoundsFetch: boolean;
	hasFirstClientDataLoaded: boolean;
	bounds: Bounds | null;
	mapCenter: { lat: number; lng: number } | null;
	mapZoom: number | null;
	isZoomChanging: boolean;
	currentLimit: number;
	isAtLimit: boolean;
	totalCountWithoutBounds: number | null;
	mapId: string;
	mode: MapMode;
	mapTypeId: "roadmap" | "satellite" | "hybrid" | "terrain";
	isFullScreen: boolean;
	setMapData: (data: MapItemWithCoodId[]) => void;
	setBounds: (bounds: Bounds | null) => void;
	setMapCenter: (center: { lat: number; lng: number }) => void;
	setMapZoom: (zoom: number | null) => void;
	setIsZoomChanging: (changing: boolean) => void;
	setMode: (mode: MapMode) => void;
	setMapTypeId: (type: "roadmap" | "satellite" | "hybrid" | "terrain") => void;
	setIsFullScreen: (isFullScreen: boolean) => void;
	fetchMapData: (params: FetchMapDataParams) => Promise<void>;
	fetchTotalCount: (params: Omit<FetchMapDataParams, "skip" | "limit" | "append">) => Promise<void>;
	fetchNearByProjects: (params: FetchMapDataParams) => Promise<void>;
	setNearByProjects: (data: MapItemWithCoodId[]) => void;
}

export function createMapDataStore({
	initialMapData = [],
	initialBounds = null,
	initialMapCenter = null,
	initialMapZoom = null,
	mapId = "sellerUnitListMap",
	initialTotalCount = null,
}: {
	initialMapData?: MapItemWithCoodId[];
	initialBounds?: Bounds | null;
	initialMapCenter?: { lat: number; lng: number } | null;
	initialMapZoom?: number | null;
	mapId?: string;
	initialTotalCount?: number | null;
} = {}) {
	// Guard against stale fetch results with a simple token system
	let fetchTokenCounter = 0;
	let activeFetchToken = 0;
	return create<MapDataState>()(
		devtools(
			subscribeWithSelector(
				immer((set, _get) => ({
					mapData: initialMapData,
					initialMapData: initialMapData,
					nearByprojects: [],
					isMapDataFetching: true,
					isNearByProjectDataFetching: true,
					isTotalCountFetching: false,
					isInitialBoundsFetch: true,
					hasFirstClientDataLoaded: false,
					bounds: initialBounds,
					mapCenter: initialMapCenter,
					mapZoom: initialMapZoom,
					isZoomChanging: false,
					currentLimit: 50, // Default SSR limit
					isAtLimit: false,
					totalCountWithoutBounds: initialTotalCount, // Set from SSR
					mapId,
					mode: "default" as MapMode,
					mapTypeId: "roadmap",
					isFullScreen: false,
					setMapData: data => {
						set(state => {
							state.mapData = data;
						});
					},
					setBounds: bounds =>
						set(state => {
							state.bounds = bounds;
						}),
					setMapCenter: center =>
						set(state => {
							state.mapCenter = center;
						}),
					setMapZoom: zoom =>
						set(state => {
							state.mapZoom = zoom;
						}),
					setIsZoomChanging: changing =>
						set(state => {
							state.isZoomChanging = changing;
						}),
					setMode: mode => {
						logger.debug("[MapStore] setMode called", { mode });
						set(state => {
							state.mode = mode;
						});
					},
					setMapTypeId: type =>
						set(state => {
							state.mapTypeId = type;
						}),
					setIsFullScreen: isFullScreen =>
						set(state => {
							state.isFullScreen = isFullScreen;
						}),
					setNearByProjects: (data: MapItemWithCoodId[]) =>
						set(state => {
							state.nearByprojects = data;
						}),
					fetchMapData: async ({
						filters,
						skip = 0,
						limit = mapDefaults.markerLimit,
						append = false,
					}: FetchMapDataParams) => {
						const myToken = ++fetchTokenCounter;
						activeFetchToken = myToken;
						set(state => {
							state.isMapDataFetching = true;
						});
						try {
							const variables = {
								params: {
									mapBasedSearch: {
										filters,
										skip,
										limit,
										calledFrom: "map-store-fetchMapData",
									},
								},
							};

							// Use dynamic import to avoid circular dependencies
							const { getApolloData } = await import("@/utils/apollo/get-data");

							const result = await getApolloData<{
								sellerUnitsAndProjects: {
									mapBasedSearch: {
										data: MapItemWithCoodId[];
										locations: ApolloLocation[];
									};
								};
							}>({
								query: GET_SELLER_UNITS_AND_PROJECTS_MAP_BASED_SEARCH,
								variables,
							});

							const mapData = result.data?.sellerUnitsAndProjects?.mapBasedSearch?.data || [];

							// Optimized map data processing with reduced allocations
							const processedMapData: MapItemWithCoodId[] = [];
							for (const item of mapData) {
								if (typeof item === "object" && item !== null && "_id" in item) {
									const typedItem = item as MapItemWithCoodId | MapItem;
									if (typeof typedItem._id === "string") {
										processedMapData.push({
											...typedItem,
											_id: typedItem._id.split(",").map(Number),
										} as MapItemWithCoodId);
									} else {
										processedMapData.push(typedItem as MapItemWithCoodId);
									}
								}
							}

							// Ignore stale results
							if (myToken !== activeFetchToken) {
								return;
							}
							set(state => {
								let nextMapData: MapItemWithCoodId[] = processedMapData;

								if (!state.hasFirstClientDataLoaded) {
									const initialSupIds = new Set<string>();
									for (const item of state.mapData) {
										const sups = item.sups || [];
										for (const sup of sups) {
											if (sup?.isInitialData && sup?._id) {
												initialSupIds.add(sup._id);
											}
										}
									}

									if (initialSupIds.size > 0) {
										nextMapData = processedMapData.map(item => {
											const sups = item.sups || [];
											const annotatedSups = sups.map(sup => {
												if (sup?._id && initialSupIds.has(sup._id)) {
													return { ...sup, isInitialData: true };
												}
												return sup;
											});
											return { ...item, sups: annotatedSups } as MapItemWithCoodId;
										});
									}
								}

								if (append) {
									const existingIds = new Set(state.mapData.map(item => JSON.stringify(item._id)));
									const newItems = nextMapData.filter(item => !existingIds.has(JSON.stringify(item._id)));
									state.mapData = [...state.mapData, ...newItems];
								} else {
									state.mapData = nextMapData;
								}

								state.isMapDataFetching = false;
								if (!state.hasFirstClientDataLoaded) state.hasFirstClientDataLoaded = true;
								state.currentLimit = limit;

								if (state.isInitialBoundsFetch) {
									state.isInitialBoundsFetch = false;
								}

								// Optimized total calculation
								let totalUnits = 0;
								for (const item of state.mapData) {
									totalUnits += item.sups?.length || 0;
								}
								state.isAtLimit = totalUnits >= limit;
							});
						} catch (error) {
							if (
								error instanceof Error &&
								(error.name === "ChunkLoadError" || error.message.includes("Loading chunk"))
							) {
								logger.warn("Failed to load map data chunk", error);
							} else {
								logger.error("Failed to match data", error);
							}
							// Only clear loading state for the active request
							if (myToken === activeFetchToken) {
								set(state => {
									state.isMapDataFetching = false;
								});
							}
						}
					},
					fetchTotalCount: async ({ filters }: Omit<FetchMapDataParams, "skip" | "limit" | "append">) => {
						set(state => {
							state.isTotalCountFetching = true;
						});

						try {
							// Use dynamic import to avoid circular dependencies
							const { getTotalCount } = await import("@/utils/search/get-total-count");

							const totalCount = await getTotalCount(filters);

							set(state => {
								state.totalCountWithoutBounds = totalCount;
								state.isTotalCountFetching = false;
							});
						} catch (error) {
							if (
								error instanceof Error &&
								(error.name === "ChunkLoadError" || error.message.includes("Loading chunk"))
							) {
								logger.warn("Failed to load total count chunk", error);
							} else {
								logger.error("Failed to fetch total count in map store", error);
							}
							set(state => {
								state.isTotalCountFetching = false;
								state.totalCountWithoutBounds = null;
							});
						}
					},
					fetchNearByProjects: async ({ filters, limit = mapDefaults.markerLimit }: FetchMapDataParams) => {
						set(state => {
							state.isNearByProjectDataFetching = true;
						});
						try {
							// Get current map center for nearby projects query
							let { bounds: _b = [{ north: 0, east: 0, west: 0, south: 0 }] } = filters ?? {};
							let bounds = _b[0];

							// Transform frontend filters to backend GraphQL format
							// This converts fields like levelType -> level, removes invalid fields, etc.
							const { transformFilters } = await import("@/utils/search/transform-filters");
							const { filtersForGraphQL } = transformFilters({
								filters,
								mapBounds: bounds,
								mapZoom: filters.zoom,
								selectedLocationId: filters.selectedLocationId,
								from: "postSSR", // Use postSSR since we already have bounds
							});

							// Remove bounds from transformed filters as it's handled at getNearbyProjectsWithSUPs level
							const { bounds: _transformedBounds, zoom: _transformedZoom, ...supFilters } = filtersForGraphQL;

							// Prepare variables for the new merged endpoint
							// All parameters including supFilters are now part of the getNearbyProjectsWithSUPs filter
							let centerLng = (bounds.east + bounds.west) / 2;
							// Handle antimeridian crossing (180° longitude)
							if (bounds.east < bounds.west) {
								centerLng = (bounds.east + bounds.west + 360) / 2;
								if (centerLng > 180) {
									centerLng -= 360;
								}
							}

							// Calculate radius as distance from center to corner of bounds rectangle
							// This ensures all points within the bounds are included
							const latDiff = Math.abs(bounds.north - bounds.south);
							const lngDiffForRadius = Math.abs(bounds.east - bounds.west);
							const latDiffMeters = (latDiff / 2) * 111320; // Half height in meters
							const lngDiffMeters =
								(lngDiffForRadius / 2) * 111320 * Math.cos((((bounds.north + bounds.south) / 2) * Math.PI) / 180); // Half width in meters (adjusted for latitude)

							// Distance from center to corner using Pythagorean theorem
							const diagonalRadius = Math.ceil(Math.sqrt(latDiffMeters ** 2 + lngDiffMeters ** 2));

							// Cap at 10km to prevent fetching too many projects
							// For project mode at zoom 17, typical bounds are ~1-2km
							const maxDistance = Math.min(Math.max(diagonalRadius, 1000), 10000);

							const variables = {
								filters: {
									getNearbyProjectsWithSUPs: {
										bounds,
										latitude: String((bounds.north + bounds.south) / 2),
										longitude: String(centerLng),
										maxDistance,
										limit,
										supFilters: {
											mapBasedSearch: {
												filters: supFilters,
											},
										},
									},
								},
							};

							// Use dynamic import to avoid circular dependencies
							const { getApolloData } = await import("@/utils/apollo/get-data");
							// Use the new merged endpoint that returns projects with SUPs already merged
							const result = await getApolloData<{
								projects: {
									getNearbyProjectsWithSUPs: RawNearbyProject[];
								};
							}>({
								query: PROJECT_NEARBY_WITH_SUPS_QUERY,
								variables,
								fetchPolicy: "no-cache",
								errorPolicy: "all",
							});

							const rawProjects = result.data?.projects?.getNearbyProjectsWithSUPs || [];
							// Parse string coordinates to number array and validate
							const validatedMapData = rawProjects
								.map(project => {
									// Parse "lng,lat" string to [lng, lat] number array
									const coordsStr = project._id;
									if (typeof coordsStr !== "string" || !coordsStr) {
										logger.warn(
											"[fetchNearByProjects] Invalid coordinates format (not a string):",
											project.projectName,
											coordsStr,
										);
										return null;
									}

									const parts = coordsStr.split(",");
									if (parts.length !== 2) {
										logger.warn(
											"[fetchNearByProjects] Invalid coordinates format (wrong parts count):",
											project.projectName,
											coordsStr,
										);
										return null;
									}

									const lng = parseFloat(parts[0]);
									const lat = parseFloat(parts[1]);

									if (isNaN(lng) || isNaN(lat) || !isFinite(lng) || !isFinite(lat)) {
										logger.warn("[fetchNearByProjects] Invalid coordinate values:", project.projectName, { lng, lat });
										return null;
									}

									// Return project with parsed coordinates
									return {
										...project,
										_id: [lng, lat] as [number, number],
									} as MapItemWithCoodId;
								})
								.filter((project): project is MapItemWithCoodId => project !== null);

							set(state => {
								state.nearByprojects = validatedMapData;
								state.isNearByProjectDataFetching = false;
							});
						} catch (error) {
							if (
								error instanceof Error &&
								(error.name === "ChunkLoadError" || error.message.includes("Loading chunk"))
							) {
								logger.warn("Failed to load nearby projects chunk", error);
							} else {
								logger.error("Failed to fetch nearby projects with SUPs", filters, error);
							}
							set(state => {
								state.isNearByProjectDataFetching = false;
							});
						}
					},
				})),
			),
			{ name: "searchPage-mapData-store" },
		),
	);
}
